import React, { useCallback } from 'react';
import { Chip, useTheme } from 'react-native-paper';
import { DrawerNavigationProp } from '@react-navigation/drawer';
import { CompositeScreenProps, useNavigation } from '@react-navigation/native';
import { Linking, View } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { t } from 'i18n-js';
import { useI18n } from '../../context/I18nContext';
import { screenNames } from '../../navigators/screenNames';
import ScrollablePage from '../../components/ui/ScrollablePage';
import { AppDrawerScreenProp } from '../../components/DrawerMenuContent';
import { AuthState, useAuth } from '../../context/AuthContext';
import { ListItemRow, ListItemRowProps } from '../../components/ListItemRow';
import {
  Currency,
  PodStatus,
  PoisType,
  Product,
  ProductPaymentType,
  ProductSubscription,
  ProductSubscriptionStatus,
  Reservation,
} from '../../types/appsync-types';
import Typography from '../../components/Typography';
import { UiIcon } from '../../components/SimpleListIcon';
import {
  useGetMyUnpaidReservationsForProductsList,
  useGetProductsByPodId,
  useMyProductReservations,
} from '../../apis/subscriptionApis';
import Loading from '../../components/Loading';
import { useMapViewFilter } from '../../context/MapViewFilterContext';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import { MapStackRouteParams } from '../../navigators/MapStackNavigator';
import { useGetPoisByParentId, useGetSpaceTerms } from '../../apis/mapApis';
import { useParkingTermsUrlForCurrentLocale } from '../../hooks/useParkingTermsUrlForCurrentLocale';

function PoisItem({
  title,
  poisId,
  isReservedToMe,
  active,
  status,
  endsAt,
  reservation,
}: // startDate,
{
  title: string;
  poisId: string;
  isReservedToMe: boolean;
  active: boolean;
  status: PodStatus | undefined;
  endsAt: string | undefined | null;
  reservation: ProductSubscription | null | undefined;
  // startDate: string | undefined | null;
}) {
  const { formatDate } = useI18n();
  const { colors } = useTheme();
  const { I18n } = useI18n();
  const { timeZone } = useMapViewFilter();
  const navigation = useNavigation<DrawerNavigationProp<any>>();

  const onPress = useCallback(() => {
    if (isReservedToMe) {
      navigation.navigate(screenNames.ReservationProductDetailScreen, {
        podId: poisId,
      });
    } else if (active) {
      navigation.navigate(screenNames.OneTimePurchaseProductsScreen, {
        podId: poisId,
        podType: PoisType.LOCKER,
      });
    }
  }, [active, isReservedToMe, navigation, poisId]);

  let subscriptionStatusText: string | null = null;
  let icon: ListItemRowProps['leftIcon'];

  switch (status) {
    case PodStatus.FREE:
      subscriptionStatusText = I18n.t('lockerInfo.status.free');
      icon = { name: 'progress-clock', set: 'material-community' };
      break;
    case PodStatus.RESERVED:
    case PodStatus.RESERVED_BY_OTHER:
      subscriptionStatusText = I18n.t('lockerInfo.status.reserved');
      icon = { name: 'alert-circle', set: 'material-community' };
      break;
    case PodStatus.COMING_SOON:
      subscriptionStatusText = I18n.t('lockerInfo.status.comingSoon');
      icon = { name: 'progress-clock', set: 'material-community' };
      break;
    case PodStatus.UNAVAILABLE:
      subscriptionStatusText = I18n.t('oneTimePurchase.failed');
      icon = { name: 'close-circle', set: 'material-community' };
      break;
    case PodStatus.RESERVED_BY_ME:
      subscriptionStatusText = reservation
        ? I18n.t('oneTimePurchase.activeSince', {
            date: reservation.startDate ? formatDate(new Date(reservation.startDate), 'timeAndDate', timeZone) : '',
          })
        : I18n.t('oneTimePurchase.activeUntil', {
            date: endsAt ? formatDate(new Date(endsAt), 'timeAndDate', timeZone) : '',
          });
      icon = { name: 'check', set: 'material' };
      break;
    default:
      icon = { name: 'progress-clock', set: 'material-community' };
      break;
  }

  const renderTitle = () => {
    return (
      <Typography variant="body1">
        {title}
        {!!subscriptionStatusText && (
          <Typography variant="body1">
            {' • '}
            <Typography variant="body1" color={colors.primary} style={{ fontWeight: 'bold' }}>
              {subscriptionStatusText}
            </Typography>
          </Typography>
        )}
      </Typography>
    );
  };

  return (
    <ListItemRow
      title={renderTitle}
      description={poisId}
      leftIcon={icon}
      onPress={onPress}
      accessibilityRole="button"
      type={active ? 'navigation' : 'default'}
    />
  );
}

export type LockerListingRouteParams = {
  filter?: string;
  podId: string;
};

type Props = CompositeScreenProps<StackScreenProps<MapStackRouteParams, 'LockerListingScreen'>, AppDrawerScreenProp>;

export const LockerListingScreen: React.FC<Props> = ({ route }) => {
  const { I18n } = useI18n();
  const { colors } = useTheme();
  const { state } = useAuth();
  const { filter: filterParam, podId } = route.params || {};
  const [filter, setFilter] = React.useState<string | null>(filterParam || null);

  const getPoisResult = useGetPoisByParentId(podId!, {});
  useRefetchOnFocus(getPoisResult.refetch);

  // TODO: Add unpaid booking handling here maybe?
  const {
    isLoading: isProductReservationsQueryLoading,
    ongoingReservations: ongoingProductReservations,
    unpaidReservations: unpaidProductReservations,
    upcomingReservations: upcomingProductReservations,
    rest: restReservations,
  } = useMyProductReservations();

  // Fetch locker group terms
  const getSpaceTermsResult = useGetSpaceTerms(podId);
  const parkingTermsUrl = useParkingTermsUrlForCurrentLocale(
    getSpaceTermsResult.data?.data?.getPoisByID?.metadata?.parkingTerms,
  );

  const pois = getPoisResult.data?.data?.getPoisByParentId ?? [];

  const isActiveBookings = filterParam === 'active';

  // TODO: Filter active locker reservations
  const filteredPois = pois;
  // const filteredPois = filter
  //   ? pois.filter((sub) => (filter === 'active' ? !!sub.rse : !sub.subscription))
  //   : pois;

  const renderNoPoisContent = () => {
    return getPoisResult.isLoading ? (
      <Loading />
    ) : (
      <Typography variant="body1" style={{ paddingHorizontal: 16, paddingVertical: 8 }}>
        {filter === 'active' ? t('lockerInfo.list.noActiveLockerReservations') : t('lockerInfo.list.noLockers')}
      </Typography>
    );
  };

  return (
    <ScrollablePage
      summary={{
        title: I18n.t('lockerInfo.list.title'),
        description: isActiveBookings
          ? I18n.t('lockerInfo.list.activeSummaryDescription')
          : I18n.t('lockerInfo.list.summaryDescription'),
        icon: isActiveBookings ? 'clock' : 'basket',
        button: parkingTermsUrl
          ? {
              label: I18n.t('lockerInfo.list.terms'),
              onPress: () => {
                Linking.openURL(parkingTermsUrl);
              },
            }
          : undefined,
      }}
    >
      {/* {state !== AuthState.GUEST_LOGIN && !filterParam && (
        <View style={{ flexDirection: 'row', paddingLeft: 16, paddingBottom: 8, alignItems: 'center' }}>
          <UiIcon size={24} name="sliders" set="fontawesome6" color={colors.onSurface} />
          {['active', 'inactive'].map((item) => {
            const selected = filter === item;
            return (
              <Chip
                key={item}
                style={{ marginLeft: 8, backgroundColor: selected ? colors.primary : undefined }}
                mode={selected ? 'flat' : 'outlined'}
                selectedColor={selected ? colors.inverseOnSurface : undefined}
                selected={selected}
                onPress={(e) => (selected ? setFilter(null) : setFilter(item))}
              >
                {I18n.t(`oneTimePurchase.filter.${item}`)}
              </Chip>
            );
          })}
        </View>
      )} */}
      {filteredPois.length || !filter || filter === 'active' ? (
        <>
          {/* {unpaidReservations?.map((reservation) => (
            <PoisItem
              key={reservation.id}
              name={reservation.product?.name ?? ''}
              productId={reservation.product?.id ?? ''}
              owner={reservation.product?.owner?.name}
              currency={(reservation?.currency as Currency) || Currency.EUR}
              active={true}
              status={reservation.status}
              endsAt={reservation.endsAt}
              startDate={reservation?.startDate}
              isReservedToMe={true}
            />
          ))} */}
          {filteredPois?.map((poi) => (
            <PoisItem
              key={poi.id}
              title={poi.title ?? ''}
              poisId={poi.id ?? ''}
              active={
                poi.availabilityStatus?.status === PodStatus.FREE ||
                poi.availabilityStatus?.status === PodStatus.RESERVED_BY_ME
              }
              status={poi.availabilityStatus?.status ?? PodStatus.UNAVAILABLE}
              endsAt={poi.availabilityStatus?.nextFreeStart}
              isReservedToMe={poi.availabilityStatus?.status === PodStatus.RESERVED_BY_ME}
              reservation={ongoingProductReservations?.find((reservation) =>
                reservation?.product?.linkedPods?.some((linkedPod) => linkedPod?.localRef === poi.localRef),
              )}
            />
          ))}
        </>
      ) : (
        renderNoPoisContent()
      )}
    </ScrollablePage>
  );
};
